/**
 * Estilos compartilhados entre TableV2 (DataGrid) e ExpandableTableV2 (TanStack + MUI Table)
 * para manter paridade visual entre telas V2.
 */

/** Tipografia das células — alinhada ao padrão MUI DataGrid */
export const TABLE_V2_CELL_FONT_SIZE = '0.8125rem';
export const TABLE_V2_HEADER_FONT_SIZE = '0.75rem';
export const TABLE_V2_CELL_LINE_HEIGHT = '1.43';
export const TABLE_V2_ROW_HEIGHT_DEFAULT = 44;
/** Altura do column header no DataGrid v8 (linhas de dados usam rowHeight) */
export const TABLE_V2_HEADER_HEIGHT_DEFAULT = 50;
export const TABLE_V2_ROW_HEIGHT_COMPACT = 36;
export const TABLE_V2_HEADER_HEIGHT_COMPACT = 40;

export const tableV2ContainerSx = {
  backgroundColor: 'background.paper',
  borderRadius: 3,
  boxShadow: (theme) => (theme.palette.mode === 'dark' ? 3 : 1),
};

export const tableV2HeaderActionsBoxSx = {
  display: 'flex',
  justifyContent: 'flex-end',
  p: 2,
};

export const tableV2HeaderButtonSx = (index) => (index > 0 ? { ml: 1 } : undefined);

/** Área da grade com altura fixa — uso no TableV2 (DataGrid preenche o espaço) */
export const tableV2AreaSx = {
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  height: {
    xs: 'calc(100vh - 320px)',
    md: 'calc(100vh - 260px)',
  },
  minHeight: 420,
  borderRadius: 2,
  overflow: 'hidden',
};

/** Tabela embutida (Widget / subtabela) — altura = conteúdo, sem viewport */
export const tableV2AreaAutoHeightSx = {
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  minHeight: 180,
  borderRadius: 2,
  overflow: 'hidden',
};

/** Lista completa (sem paginar): altura definida para o virtualScroller do DataGrid rolar */
export const tableV2AreaListScrollSx = {
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  height: {
    xs: '60vh',
    md: '65vh',
  },
  minHeight: 320,
  borderRadius: 2,
  overflow: 'hidden',
};

/** Lista completa no widget: cresce com as linhas e rola se passar do viewport */
export const tableV2AreaAutoHeightScrollSx = {
  ...tableV2AreaAutoHeightSx,
  maxHeight: {
    xs: '70vh',
    md: 'calc(100vh - 240px)',
  },
  overflow: 'auto',
};

const TABLE_V2_AREA_MAX_HEIGHT = {
  xs: 'calc(100vh - 320px)',
  md: 'calc(100vh - 260px)',
};

/**
 * Área da grade — ExpandableTableV2.
 * Mesma altura mínima/viewport do TableV2; o corpo da tabela cresce com flex dentro do card.
 */
export const tableV2AreaContentFitSx = {
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  height: TABLE_V2_AREA_MAX_HEIGHT,
  minHeight: 420,
  maxHeight: TABLE_V2_AREA_MAX_HEIGHT,
  borderRadius: 2,
  overflow: 'hidden',
};

export const tableV2LoadingBodyFitSx = (loading) => ({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: 0,
  opacity: loading ? 0.38 : 1,
  transition: (theme) =>
    theme.transitions.create('opacity', {
      duration: theme.transitions.duration.shorter,
    }),
  pointerEvents: loading ? 'none' : 'auto',
});

/** Container rolável da tabela MUI — preenche o espaço acima da paginação */
export const tableV2TableContainerFitSx = {
  flex: 1,
  minHeight: 0,
  overflow: 'auto',
};

export const tableV2LoadingLinearSx = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  zIndex: 4,
  height: 3,
};

export const tableV2LoadingBodySx = (loading) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  minHeight: 0,
  opacity: loading ? 0.38 : 1,
  transition: (theme) =>
    theme.transitions.create('opacity', {
      duration: theme.transitions.duration.shorter,
    }),
  pointerEvents: loading ? 'none' : 'auto',
});

/** Espelha TableV2: sem outline de foco nas células */
export const tableV2CellFocusSx = {
  '&:focus, &:focus-within': {
    outline: 'none',
  },
};

export const tableV2TableSx = {
  border: 'none',
  width: '100%',
  '& .MuiTableCell-root': {
    borderColor: 'divider',
    boxSizing: 'border-box',
    ...tableV2CellFocusSx,
  },
};

export const getTableV2HeaderCellSx = (
  headerHeight = TABLE_V2_HEADER_HEIGHT_DEFAULT
) => ({
  backgroundColor: (theme) =>
    theme.palette.mode === 'dark'
      ? theme.palette.neutral[900]
      : theme.palette.neutral[50],
  fontWeight: 600,
  fontSize: TABLE_V2_HEADER_FONT_SIZE,
  lineHeight: TABLE_V2_CELL_LINE_HEIGHT,
  color: 'text.primary',
  height: headerHeight,
  minHeight: headerHeight,
  maxHeight: headerHeight,
  py: 0,
  px: '10px',
  borderBottom: '1px solid',
  borderColor: 'divider',
  ...tableV2CellFocusSx,
});

export const getTableV2BodyCellSx = (rowHeight = TABLE_V2_ROW_HEIGHT_DEFAULT) => ({
  fontSize: TABLE_V2_CELL_FONT_SIZE,
  lineHeight: TABLE_V2_CELL_LINE_HEIGHT,
  fontWeight: 400,
  color: 'text.primary',
  height: rowHeight,
  minHeight: rowHeight,
  maxHeight: rowHeight,
  py: 0,
  px: '10px',
  verticalAlign: 'middle',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  ...tableV2CellFocusSx,
});

/** Zebra sem hover manual — usar com `<TableRow hover />` */
export const getTableV2MasterRowSx = (index) => ({
  backgroundColor: (theme) =>
    index % 2 === 0
      ? theme.palette.mode === 'dark'
        ? theme.palette.neutral[900]
        : theme.palette.neutral[50]
      : theme.palette.mode === 'dark'
        ? theme.palette.neutral[800]
        : theme.palette.neutral[100],
});

/** @deprecated Use getTableV2MasterRowSx + TableRow hover */
export const getTableV2RowZebraSx = (index) => ({
  ...getTableV2MasterRowSx(index),
  '&:hover': {
    backgroundColor: (theme) => theme.palette.action.hover,
  },
});

/** Conteúdo textual dentro de células custom (renderCell / TanStack cell) */
export const tableV2CellContentSx = {
  fontSize: TABLE_V2_CELL_FONT_SIZE,
  lineHeight: TABLE_V2_CELL_LINE_HEIGHT,
  color: 'text.primary',
};

/** Botões de ação compactos na coluna Ações do DataGrid */
export const tableV2ActionCellSx = {
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'nowrap',
  gap: 0,
  width: '100%',
  height: '100%',
  '& .MuiIconButton-root': {
    p: '2px',
    width: 28,
    height: 28,
  },
};

/** Largura estimada por botão na coluna Ações (px) */
export const TABLE_V2_ACTION_BUTTON_WIDTH = 30;

/** Conteúdo empilhado (várias linhas) centralizado na altura da célula */
export const tableV2StackedCellContentSx = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  height: '100%',
  minWidth: 0,
  width: '100%',
  whiteSpace: 'normal',
  wordBreak: 'break-word',
};

/** DataGrid: permite quebra de linha e altura automática (paridade com tabelas V1) */
export const tableV2MultilineGridSx = {
  '& .MuiDataGrid-cell': {
    whiteSpace: 'normal',
    lineHeight: TABLE_V2_CELL_LINE_HEIGHT,
    alignItems: 'flex-start',
    py: 1,
    overflow: 'visible',
    textOverflow: 'clip',
  },
};

export const tableV2CellCaptionSx = {
  mt: 0.25,
  fontSize: '0.75rem',
  lineHeight: 1.33,
  color: 'primary.main',
};

/** Chips de contagem (locais, tamanhos) — altura compatível com rowHeight 52 */
export const tableV2ChipSx = {
  height: 24,
  maxHeight: 24,
  fontWeight: 500,
  color: 'text.primary',
  borderColor: 'divider',
  '& .MuiChip-label': {
    px: 1,
    fontSize: '0.8125rem',
    lineHeight: 1.25,
  },
};

export const tableV2PaginationSx = {
  flexShrink: 0,
  borderTop: '1px solid',
  borderColor: 'divider',
  color: 'text.primary',
  '& .MuiTablePagination-toolbar': {
    minHeight: 44,
    px: 1,
  },
  '& .MuiTablePagination-selectLabel': {
    fontSize: TABLE_V2_CELL_FONT_SIZE,
    color: 'text.secondary',
    mb: 0,
    mt: 0,
  },
  '& .MuiTablePagination-displayedRows': {
    fontSize: TABLE_V2_CELL_FONT_SIZE,
    color: 'text.secondary',
    mb: 0,
    mt: 0,
  },
  '& .MuiTablePagination-select': {
    fontSize: TABLE_V2_CELL_FONT_SIZE,
  },
  '& .MuiTablePagination-actions': {
    color: 'text.secondary',
  },
  '& .MuiIconButton-root': {
    color: 'text.secondary',
  },
};

export const tableV2EmptyStateSx = {
  py: 6,
  textAlign: 'center',
  fontSize: TABLE_V2_CELL_FONT_SIZE,
  lineHeight: TABLE_V2_CELL_LINE_HEIGHT,
  color: 'text.secondary',
};

export const tableV2SkeletonCellSx = (rowHeight = TABLE_V2_ROW_HEIGHT_DEFAULT) => ({
  height: rowHeight,
  minHeight: rowHeight,
  py: 0,
  px: '10px',
  borderBottom: '1px solid',
  borderColor: 'divider',
});

/** Linha master quando o painel de detalhe está aberto */
export const getTableV2ExpandedMasterRowSx = (baseZebraSx) => ({
  ...baseZebraSx,
  borderLeft: '3px solid',
  borderLeftColor: 'primary.main',
  backgroundColor: (theme) =>
    theme.palette.mode === 'dark'
      ? `${theme.palette.primary.main}14`
      : `${theme.palette.primary.main}0D`,
});

/** Container do painel de detalhe (sub-tabela expansível) */
export const tableV2DetailPanelWrapperSx = {
  mx: { xs: 1, sm: 2 },
  my: 1,
  pb: 1,
  borderRadius: 2,
  border: '1px solid',
  borderColor: 'divider',
  borderLeftWidth: 3,
  borderLeftColor: 'primary.main',
  backgroundColor: (theme) =>
    theme.palette.mode === 'dark'
      ? theme.palette.neutral[800]
      : theme.palette.neutral[100],
  boxShadow: (theme) => (theme.palette.mode === 'dark' ? 0 : 1),
  overflow: 'hidden',
};

export const tableV2DetailPanelTitleSx = {
  px: 2,
  pt: 1.5,
  pb: 0.5,
  fontSize: '0.75rem',
  fontWeight: 600,
  lineHeight: 1.33,
  color: 'text.secondary',
  borderBottom: '1px solid',
  borderColor: 'divider',
};

/** Célula da sub-tabela dentro do painel — fundo paper para contraste com o container */
export const getTableV2DetailBodyCellSx = (
  rowHeight,
  rowIndex = 0
) => ({
  ...getTableV2BodyCellSx(rowHeight),
  backgroundColor: (theme) =>
    rowIndex % 2 === 0
      ? theme.palette.background.paper
      : theme.palette.mode === 'dark'
        ? theme.palette.neutral[900]
        : theme.palette.neutral[50],
});

/** Linha do TableRow que envolve o colspan do painel de detalhe */
export const tableV2DetailPanelRowSx = {
  p: 0,
  py: 0.5,
  borderBottom: '1px solid',
  borderColor: 'divider',
  backgroundColor: 'transparent',
};
